import { useGetAppointmentDayCounts } from '@pa/api/clinic'
import { addDays, Field, Select, Toggle, useToast } from '@pa/ui'
import { keepPreviousData } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarOff,
  CalendarPlus,
  ChevronDown,
  Clock,
  Copy,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
} from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { clinicToday, fmtLocalDate } from '../model/dates'
import { LOCAL_HOLIDAY_PREFIX } from '../model/draft'
import { DAY_NAMES, dayOf, hoursSummary, toMinutes, WEEK_ORDER } from '../model/hours'
import { canOpenDay, draftPfWindow, timeOptionsIn } from '../model/pfWindow'
import type { DayHours, Holiday, Settings } from '../model/types'
import { pfProblem, windowLabel } from '../model/validate'
import { useSettingsDraft } from '../SettingsDraftProvider'

/** UserFlows C-10 "Practice Fusion hours" copy (D-75). */
export const PF_HOURS_COPY = {
  closed: 'Closed in Practice Fusion',
  closedTip: 'No shown location is open on this day in Practice Fusion. Change it in PF, then Refresh from PF.',
  turnOff: 'Closed in Practice Fusion. Turn this day off.',
  outside: (label: string) => `Must be inside Practice Fusion's hours (${label})`,
  /** While a super admin has the testing switch on (`testingIgnorePfHours`). */
  ignored: 'Practice Fusion hours are ignored (testing only).',
}

/**
 * C-10 Clinic profile (UserFlows C-10): contact details with the "Patients see this" preview, opening hours and
 * holidays. Saved with the rest of the Settings draft. "Today" is the clinic's (D-21). Upcoming holidays, saved or
 * not, carry the "N appointments already booked" tag from #18 (task 09).
 */
export function ClinicProfileTab({ timeZone }: { timeZone: string }) {
  const { draft, update, clinicId } = useSettingsDraft()
  return (
    <div className="profile-tab">
      <ContactCard draft={draft} update={update} />
      <HoursCard draft={draft} update={update} />
      <HolidaysCard draft={draft} update={update} today={clinicToday(timeZone)} clinicId={clinicId} />
    </div>
  )
}

interface CardProps {
  draft: Settings
  update: (fn: (d: Settings) => Settings) => void
}

function ContactCard({ draft, update }: CardProps) {
  const c = draft.profile.contact
  const ids = { phone: useId(), email: useId(), address: useId() }
  const patch = (p: Partial<Settings['profile']['contact']>) => {
    update((d) => ({ ...d, profile: { ...d.profile, contact: { ...d.profile.contact, ...p } } }))
  }
  return (
    <section aria-labelledby={`${ids.phone}-h`}>
      <h2 className="profile-h" id={`${ids.phone}-h`}>
        Contact
      </h2>
      <div className="contact-tab">
        <div className="card card-pad set-form">
          <Field label="Phone" hint="Patients tap to call." htmlFor={ids.phone}>
            <div className="input-wrap contact-input">
              <Phone aria-hidden />
              <input
                id={ids.phone}
                className="input"
                type="tel"
                maxLength={40}
                value={c.phone}
                onChange={(e) => {
                  patch({ phone: e.target.value })
                }}
              />
            </div>
          </Field>
          <Field label="Email" htmlFor={ids.email}>
            <div className="input-wrap contact-input">
              <Mail aria-hidden />
              <input
                id={ids.email}
                className="input"
                type="email"
                maxLength={254}
                value={c.email}
                onChange={(e) => {
                  patch({ email: e.target.value })
                }}
              />
            </div>
          </Field>
          <Field label="Address" htmlFor={ids.address}>
            <div className="input-wrap contact-input">
              <MapPin aria-hidden />
              <input
                id={ids.address}
                className="input"
                maxLength={300}
                value={c.address}
                onChange={(e) => {
                  patch({ address: e.target.value })
                }}
              />
            </div>
          </Field>
        </div>
        <div className="card card-pad contact-preview" aria-label="Patients see this" role="region">
          <div className="label preview-label">Patients see this</div>
          <p className="hint">
            For new patients, when a record isn&apos;t found, and whenever online booking can&apos;t go ahead.
          </p>
          <div className="contact-block compact-preview">
            <div className="cb-row">
              <span className="cb-ic">
                <Phone aria-hidden />
              </span>
              <span>
                <small>Call us</small>
                <b>{c.phone || '—'}</b>
              </span>
            </div>
            <div className="cb-row">
              <span className="cb-ic">
                <Mail aria-hidden />
              </span>
              <span>
                <small>Email us</small>
                <b>{c.email || '—'}</b>
              </span>
            </div>
            <div className="cb-row">
              <span className="cb-ic">
                <MapPin aria-hidden />
              </span>
              <span>
                <small>Visit us</small>
                <b>{c.address || '—'}</b>
              </span>
            </div>
            <div className="cb-row">
              <span className="cb-ic">
                <Clock aria-hidden />
              </span>
              <span>
                <small>Opening hours</small>
                {hoursSummary(draft.profile.hours).map((l) => (
                  <b key={l}>{l}</b>
                ))}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function HoursCard({ draft, update }: CardProps) {
  const toast = useToast()
  const hours = draft.profile.hours
  // D-75: the PF window of the draft's shown locations.
  const window = draftPfWindow(draft)
  const patch = (weekday: number, p: Partial<DayHours>) => {
    update((d) => ({
      ...d,
      profile: { ...d.profile, hours: d.profile.hours.map((h) => (h.weekday === weekday ? { ...h, ...p } : h)) },
    }))
  }
  const copyToOpenDays = (from: number) => {
    const src = dayOf(hours, from)
    if (!src) return
    update((d) => ({
      ...d,
      profile: {
        ...d.profile,
        hours: d.profile.hours.map((h) => (h.open ? { ...h, start: src.start, end: src.end } : h)),
      },
    }))
    toast(`${DAY_NAMES[from] ?? 'Day'}'s hours copied to every open day. Save to apply`, 'info')
  }
  return (
    <section aria-labelledby="hours-h">
      <div className="profile-h-row">
        <div>
          <h2 className="profile-h" id="hours-h">
            Opening hours
          </h2>
          <p className="hint">
            Patients can only book inside these hours. Providers are treated as available during them unless Practice
            Fusion shows them as busy.
          </p>
          {draft.testingIgnorePfHours && (
            <p className="hint testing-note" role="note">
              <AlertTriangle aria-hidden /> {PF_HOURS_COPY.ignored}
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!dayOf(hours, 1)?.open}
          onClick={() => {
            copyToOpenDays(1)
          }}
        >
          <Copy aria-hidden /> Copy Monday to all open days
        </button>
      </div>
      <div className="card hours-card">
        {WEEK_ORDER.map((day) => {
          const h = dayOf(hours, day)
          if (!h) return null
          const name = DAY_NAMES[day]
          const bad = h.open && toMinutes(h.end) <= toMinutes(h.start)
          // D-75: a closed day that isn't PF-open can't be opened; an open one keeps its toggle so it can be closed.
          const pfClosed = !h.open && !canOpenDay(window, day)
          const dayWindow = window === null ? undefined : (window[day] ?? null)
          const pf = pfProblem(h, window)
          return (
            <div key={day} className={`hours-row ${h.open ? '' : 'closed'}`}>
              <b className="hr-day">{name}</b>
              <label className="hr-open">
                <Toggle
                  checked={h.open}
                  disabled={pfClosed}
                  onChange={(v) => {
                    patch(day, { open: v })
                  }}
                  label={`Open on ${name}`}
                />
                <span aria-hidden>{h.open ? 'Open' : 'Closed'}</span>
              </label>
              {h.open ? (
                <div className="hr-times">
                  <Select
                    label={`${name} opens`}
                    value={h.start}
                    options={timeOptionsIn(dayWindow, h.start)}
                    onChange={(v) => {
                      patch(day, { start: v })
                    }}
                  />
                  <span className="muted">to</span>
                  <Select
                    label={`${name} closes`}
                    value={h.end}
                    options={timeOptionsIn(dayWindow, h.end)}
                    onChange={(v) => {
                      patch(day, { end: v })
                    }}
                  />
                  {bad && <span className="err-text hr-bad">Closing time must be after opening time</span>}
                  {pf?.kind === 'closed' && <span className="err-text hr-bad">{PF_HOURS_COPY.turnOff}</span>}
                  {pf?.kind === 'outside' && (
                    <span className="err-text hr-bad">{PF_HOURS_COPY.outside(windowLabel(pf.window))}</span>
                  )}
                </div>
              ) : pfClosed ? (
                <span className="hr-times muted" title={PF_HOURS_COPY.closedTip}>
                  {PF_HOURS_COPY.closed}
                </span>
              ) : (
                <span className="hr-times muted">Closed all day: no online bookings</span>
              )}
              <button
                type="button"
                className="btn btn-ghost btn-icon btn-sm hr-copy"
                disabled={!h.open || bad}
                title={`Copy ${name}'s hours to every open day`}
                aria-label={`Copy ${name}'s hours to every open day`}
                onClick={() => {
                  copyToOpenDays(day)
                }}
              >
                <Copy aria-hidden />
              </button>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/** UserFlows C-10 Holidays: the warning tag on an upcoming holiday with bookings (#18). */
export const BOOKED_TAG_COPY = {
  tag: (n: number) => `${String(n)} appointment${n === 1 ? '' : 's'} already booked`,
  tip: "Adding a holiday doesn't cancel these. Contact the patients to rebook.",
} as const
/** API.md §6.5: `from` and `to` at most 400 days apart. */
const DAY_COUNTS_MAX_DAYS = 400

/**
 * #18 for today → the latest upcoming holiday (≤ 400 days), so unsaved holidays get their tag too: the count for a
 * holiday is the sum over its date and its `locationIds` (all locations when `null`, API.md §6.5).
 */
function useBookedCounts(clinicId: string, today: string, upcoming: Holiday[]) {
  const last = upcoming.reduce((max, h) => (h.date > max ? h.date : max), today)
  const cap = addDays(today, DAY_COUNTS_MAX_DAYS)
  const to = last < cap ? last : cap
  const query = useGetAppointmentDayCounts(
    clinicId,
    { from: today, to },
    { query: { enabled: upcoming.length > 0, placeholderData: keepPreviousData } },
  )
  const items = query.data?.items ?? []
  return (h: Holiday): number =>
    items
      .filter((i) => i.date === h.date && (h.locationIds === null || h.locationIds.includes(i.locationId)))
      .reduce((n, i) => n + i.count, 0)
}

function HolidaysCard({ draft, update, today, clinicId }: CardProps & { today: string; clinicId: string }) {
  const toast = useToast()
  const counter = useRef(0)
  const ids = { date: useId(), name: useId() }
  const locations = draft.locations.filter((l) => !l.removedInPf)
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const [locIds, setLocIds] = useState<string[] | null>(null) // null = all locations
  const [showPast, setShowPast] = useState(false)
  const holidays = draft.profile.holidays
  const taken = holidays.some((h) => h.date === date)
  const past = !!date && date < today
  const canAdd = !!date && !past && !taken
  const add = () => {
    if (!canAdd) return
    counter.current += 1
    const holiday: Holiday = {
      id: `${LOCAL_HOLIDAY_PREFIX}${String(counter.current)}`,
      date,
      name: name.trim(),
      locationIds: locIds,
    }
    update((d) => ({
      ...d,
      profile: {
        ...d.profile,
        holidays: [...d.profile.holidays, holiday].sort((a, b) => a.date.localeCompare(b.date)),
      },
    }))
    toast('Holiday added. Save to apply', 'info')
    setDate('')
    setName('')
    setLocIds(null)
  }
  const remove = (id: string) => {
    update((d) => ({ ...d, profile: { ...d.profile, holidays: d.profile.holidays.filter((h) => h.id !== id) } }))
  }
  const toggleLoc = (id: string) => {
    const cur = locIds ?? []
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    // Picking none or all goes back to "All locations" (UserFlows C-10).
    setLocIds(next.length === 0 || next.length === locations.length ? null : next)
  }
  const where = (locationIds: string[] | null) =>
    locationIds === null
      ? 'All locations'
      : locationIds.map((id) => draft.locations.find((l) => l.id === id)?.name ?? id).join(', ')
  const upcoming = holidays.filter((h) => h.date >= today)
  const before = holidays.filter((h) => h.date < today).reverse()
  const bookedOn = useBookedCounts(clinicId, today, upcoming)
  const row = (h: Holiday, isPast: boolean) => {
    // Only upcoming holidays carry the tag (UserFlows C-10 "Upcoming list").
    const n = isPast ? 0 : bookedOn(h)
    return (
      <div key={h.id} className={`hol-row ${isPast ? 'past' : ''}`}>
        <span className="hol-ic">
          <CalendarOff aria-hidden />
        </span>
        <div className="grow">
          <b>{fmtLocalDate(h.date)}</b>
          <span className="cell-sub">
            {h.name || 'Holiday'} · {where(h.locationIds)}
          </span>
        </div>
        {n > 0 && (
          <span className="tag tag-amber" title={BOOKED_TAG_COPY.tip}>
            {BOOKED_TAG_COPY.tag(n)}
          </span>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm"
          aria-label={`Remove ${h.name || 'holiday'} on ${h.date}`}
          title="Remove"
          onClick={() => {
            remove(h.id)
          }}
        >
          <Trash2 aria-hidden />
        </button>
      </div>
    )
  }
  return (
    <section aria-labelledby="holidays-h">
      <h2 className="profile-h" id="holidays-h">
        Holidays
      </h2>
      <p className="hint">
        Dates when online booking is closed, for the whole clinic or only some locations. Existing appointments on those
        dates aren&apos;t changed.
      </p>
      <div className="card holidays-card">
        <div className="hol-add">
          <label className="field" htmlFor={ids.date}>
            <span className="label">Date</span>
            <input
              id={ids.date}
              className="input"
              type="date"
              min={today}
              value={date}
              onChange={(e) => {
                setDate(e.target.value)
              }}
            />
          </label>
          <label className="field grow" htmlFor={ids.name}>
            <span className="label">
              Name <span className="muted">(optional)</span>
            </span>
            <input
              id={ids.name}
              className="input"
              placeholder="e.g. Thanksgiving"
              value={name}
              maxLength={60}
              onChange={(e) => {
                setName(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add()
              }}
            />
          </label>
          <div className="field hol-where" role="group" aria-label="Closed at">
            <span className="label">Closed at</span>
            <div className="chip-row">
              <button
                type="button"
                className="chip"
                aria-pressed={locIds === null}
                onClick={() => {
                  setLocIds(null)
                }}
              >
                All locations
              </button>
              {locations.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className="chip"
                  aria-pressed={!!locIds?.includes(l.id)}
                  onClick={() => {
                    toggleLoc(l.id)
                  }}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="btn btn-primary hol-btn" disabled={!canAdd} onClick={add}>
            <Plus aria-hidden /> Add holiday
          </button>
          {taken && <span className="err-text hol-msg">That date is already a holiday.</span>}
          {past && <span className="err-text hol-msg">Pick today or a later date.</span>}
        </div>
        {upcoming.length ? (
          <div className="hol-list">{upcoming.map((h) => row(h, false))}</div>
        ) : (
          <div className="empty hol-empty">
            <CalendarPlus aria-hidden />
            <div>No holidays added. Patients can book on any open day.</div>
          </div>
        )}
        {before.length > 0 && (
          <>
            <button
              type="button"
              className="hol-past-toggle"
              aria-expanded={showPast}
              onClick={() => {
                setShowPast(!showPast)
              }}
            >
              <ChevronDown aria-hidden className={showPast ? 'flip' : ''} /> {showPast ? 'Hide' : 'Show'} past holidays
              ({before.length})
            </button>
            {showPast && <div className="hol-list">{before.map((h) => row(h, true))}</div>}
          </>
        )}
      </div>
    </section>
  )
}
